import React, { useEffect, useState } from 'react';
import { Modal, Spin, Typography, Space } from 'antd';
import { ShopOutlined, CloseOutlined } from '@ant-design/icons';
import type { AuraFarmaciaResponse } from '../api/auraApi';
import { fct } from '../fct/tokens/tokens';

const { Text, Title } = Typography;

interface FarmaciaTitularesModalProps {
  visible: boolean;
  onClose: () => void;
  farmaciaId: number;
  farmaciaName: string;
  userId: number;
  modo?: 'titulares' | 'empleados';
  /** De dónde se leen: cada pantalla pasa su carga desde Gescom, Conciliación (#149) y Transmisiones (#221). */
  cargarTitulares: (farmaciaId: number, usuario: number, opcion: number) => Promise<AuraFarmaciaResponse>;
}

/** El motivo que manda el servidor, si lo manda (por ejemplo, el 409 de una acción en espera); si no, uno genérico. */
const motivoDelError = (err: unknown): string => {
  const respuesta = (err as { response?: { data?: { message?: unknown } } })?.response;
  return typeof respuesta?.data?.message === 'string' ? respuesta.data.message : 'Error al cargar los titulares';
};

const FarmaciaTitularesModal: React.FC<FarmaciaTitularesModalProps> = ({
  visible,
  onClose,
  farmaciaId,
  farmaciaName,
  userId,
  modo = 'titulares',
  cargarTitulares,
}) => {
  const opcion = modo === 'empleados' ? 6 : 2;
  const label = modo === 'empleados' ? 'Empleados en Gescom' : 'Titulares en Gescom';
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AuraFarmaciaResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible && farmaciaId && userId) {
      loadTitulares();
    }
    if (!visible) {
      setData(null);
      setError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, farmaciaId, userId, opcion]);

  const loadTitulares = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await cargarTitulares(farmaciaId, userId, opcion);
      setData(response);
    } catch (err) {
      console.error('Error loading titulares:', err);
      setError(motivoDelError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={450}
      centered
      bodyStyle={{
        padding: 0,
        backgroundColor: fct.marca['verde-tenue'],
        borderRadius: fct.radio.tarjeta,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          padding: '16px 20px',
          borderBottom: `1px solid ${fct.linea.tarjeta}`,
          backgroundColor: fct.marca['verde-tenue'],
          borderTopLeftRadius: fct.radio.tarjeta,
          borderTopRightRadius: fct.radio.tarjeta,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <ShopOutlined style={{ fontSize: 18 }} />
            <Title level={5} style={{ margin: 0, color: fct.marca['verde-oscuro'] }}>
              Farmacia
            </Title>
          </div>
          <Text style={{ color: fct.tinta['700'], fontSize: 14 }}>
            {farmaciaName || 'Sin datos'}
          </Text>
        </div>
        <CloseOutlined
          onClick={onClose}
          style={{
            fontSize: 16,
            color: fct.tinta['500'],
            cursor: 'pointer',
            padding: 4,
          }}
        />
      </div>

      {/* Body */}
      <div style={{ padding: '20px', minHeight: 120 }}>
        <Text strong style={{ display: 'block', marginBottom: 16, color: fct.marca['verde-oscuro'] }}>
          {label}
        </Text>

        {loading && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Spin />
          </div>
        )}

        {error && (
          <Text type="danger">{error}</Text>
        )}

        {!loading && !error && data && (() => {
          const personas = (modo === 'empleados' ? data.empleados : data.titulares) || [];
          return (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {personas.length > 0 ? (
              personas.map((titular) => (
                <div
                  key={titular.id}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: fct.superficie.tarjeta,
                    borderRadius: 4,
                    border: `1px solid ${fct.linea.tarjeta}`,
                  }}
                >
                  <Text>{titular.nombreCompleto}</Text>
                </div>
              ))
            ) : (
              <Text type="secondary">{modo === 'empleados' ? 'No hay empleados registrados' : 'No hay titulares registrados'}</Text>
            )}
          </Space>
          );
        })()}
      </div>
    </Modal>
  );
};

export default FarmaciaTitularesModal;
